import { toDateOnlyValue } from '@/lib/utils/custom-date/custom-date';
import { isValidPhoneNumber } from 'react-phone-number-input';
import * as z from 'zod';
import {
  EmergencyContact,
  Employee,
  EmployeeAddress,
  EmployeeCreateInput,
  HrTermInput,
} from '../types/employee.types';
import {
  basicInfoDefaults,
  basicInfoFromEmployee,
  basicInfoSchema,
  basicInfoToPayload,
} from './basic-info-schema';
import {
  contactInfoDefaults,
  contactInfoFromEmployee,
  contactInfoSchema,
  contactInfoToPayload,
  emergencyContactValues,
  presentAddressValues,
} from './contact-info-schema';
import { toDisplayName } from './display-name';
import {
  hrTermsFromRecords,
  hrTermsRefine,
  hrTermsShape,
  hrTermsToPayload,
} from './hr-term-schema';
import {
  resolveStoredSkill,
  resolveSubCategoryId,
  SkillLookup,
  skillsFromEmployee,
  skillsShape,
  skillsToPayload,
  storedSkillName,
} from './skills-schema';

// A person's profile is edited one tab at a time, each tab saving one record:
//  - "Overview"     -> `UserProfileDetail` (basic info),
//  - "Contact Info" -> business email / phone on `UserProfileDetail`, the rest (contacts, personal,
//                      academic and previous-employment details) on `UserContactDetail`,
//  - "HR Info"      -> one `UserHRPeriodicalProfile` per HR term (HR only).
// Adding a person only asks for the minimum (see `employeeCreateSchema`); roles are assigned from the
// directory list.

const requiredMessage = { message: 'ERR_MSG_REQUIRED' };

// --- Add employee (minimum, like adding a user in Blocks OS) ------------------------------------------

export const employeeCreateSchema = z.object({
  FirstName: z.string().trim().min(1, requiredMessage),
  LastName: z.string().trim().min(1, requiredMessage),
  Email: z.string().trim().min(1, requiredMessage).email({ message: 'EMAIL_INVALID' }),
  PhoneNumber: z
    .string()
    .refine((value) => !value || isValidPhoneNumber(value), { message: 'PHONE_NUMBER_INVALID' }),
  JoiningDate: z.string().min(1, requiredMessage),
  DepartmentId: z.string().min(1, requiredMessage),
  DepartmentName: z.string(),
  DepartmentKey: z.string(),
  DesignationId: z.string().min(1, requiredMessage),
  Designation: z.string(),
  EmployeeID: z.string(),
});

export type EmployeeCreateValues = z.infer<typeof employeeCreateSchema>;

export const employeeCreateDefaults: EmployeeCreateValues = {
  FirstName: '',
  LastName: '',
  Email: '',
  PhoneNumber: '',
  JoiningDate: '',
  DepartmentId: '',
  DepartmentName: '',
  DepartmentKey: '',
  DesignationId: '',
  Designation: '',
  EmployeeID: '',
};

export const createValuesToInput = (values: EmployeeCreateValues): EmployeeCreateInput => {
  const firstName = values.FirstName.trim();
  const lastName = values.LastName.trim();
  return {
    FirstName: firstName,
    LastName: lastName,
    DisplayName: toDisplayName(firstName, lastName),
    Email: values.Email.trim(),
    ...(values.PhoneNumber ? { PhoneNumber: values.PhoneNumber } : {}),
    JoiningDate: toDateOnlyValue(values.JoiningDate) ?? '',
    DepartmentId: values.DepartmentId,
    DepartmentName: values.DepartmentName,
    ...(values.DepartmentKey ? { DepartmentKey: values.DepartmentKey } : {}),
    DesignationId: values.DesignationId,
    Designation: values.Designation,
    ...(values.EmployeeID.trim() ? { EmployeeID: values.EmployeeID.trim() } : {}),
  };
};

// --- "Overview" tab (HR) ---------------------------------------------------------------------------

export const overviewSchema = basicInfoSchema;
export type OverviewValues = z.infer<typeof overviewSchema>;

/** The overview of the person with IAM user id `userId`, who cannot be their own reporting manager. */
export const overviewSchemaFor = (userId?: string) =>
  overviewSchema.refine((values) => !userId || values.ReportingManagerUserId !== userId, {
    path: ['ReportingManagerUserId'],
    message: 'REPORTING_MANAGER_CANNOT_BE_SELF',
  });

export const employeeToOverviewValues = (employee: Employee): OverviewValues => ({
  ...basicInfoDefaults,
  ...basicInfoFromEmployee(employee),
});

/** The employee id cannot change once the person exists. */
export const overviewValuesToInput = (values: OverviewValues): Record<string, unknown> => {
  const input: Record<string, unknown> = { ...basicInfoToPayload(values) };
  delete input.EmployeeID;
  return input;
};

// --- "Contact Info" tab ----------------------------------------------------------------------------

export const contactSchema = contactInfoSchema;
export type ContactValues = z.infer<typeof contactSchema>;

export const employeeToContactValues = (employee: Employee): ContactValues => ({
  ...contactInfoDefaults,
  ...contactInfoFromEmployee(employee),
});

export const contactValuesToInput = (values: ContactValues): Record<string, unknown> => ({
  ...contactInfoToPayload(values),
});

// --- "HR Info" tab (HR only) -----------------------------------------------------------------------

export const hrInfoSchema = z
  .object(hrTermsShape)
  .superRefine((values, ctx) => hrTermsRefine(values.HrTerms, ctx));

export type HrInfoValues = z.infer<typeof hrInfoSchema>;

export const employeeToHrInfoValues = (employee: Employee): HrInfoValues => ({
  ...hrTermsFromRecords(employee.HrTerms ?? []),
});

export const hrInfoValuesToTerms = (values: HrInfoValues): HrTermInput[] =>
  hrTermsToPayload(values.HrTerms, values.ActiveTermIndex);

// --- Self-service profile ("Edit profile") -----------------------------------------------------------
// A person edits their own basic info, contact details and skills. What HR owns (joining date,
// unit, reporting manager, designation, employee id, CTC level) stays read-only and is not validated
// here, and HR terms and roles are left alone.

const HR_OWNED_FIELDS = [
  'JoiningDate',
  'DepartmentId',
  'DepartmentName',
  'DepartmentKey',
  'ReportingManagerUserId',
  'ReportingManagerName',
  'DesignationId',
  'Designation',
  'EmployeeID',
  'CtcLevelId',
] as const;

const HR_OWNED_MASK = Object.fromEntries(HR_OWNED_FIELDS.map((field) => [field, true])) as Record<
  (typeof HR_OWNED_FIELDS)[number],
  true
>;

export const selfOverviewSchema = basicInfoSchema.omit(HR_OWNED_MASK);

/**
 * What a person holding `hrm::people::require-extended-info` must fill in on their own profile: the
 * legacy edit-profile required set (outside Germany), limited to fields the self-service forms always
 * show. Permanent address, references and social profiles stay optional because the contact tab only
 * shows them for some employment locations.
 */
export const EXTENDED_PROFILE_REQUIRED_FIELDS = [
  'DateOfBirth',
  'BloodGroup',
  'FathersName',
  'MothersName',
  'PassportNo',
  'Nid',
  'Etin',
  'HobbiesInterests',
  'LatestAcademicTitle',
  'GraduationInstitute',
  'PostGraduationInstitute',
  'PreviousExperience',
  'PreviousOrganization',
  'PhoneNumber',
  'PrivatePhoneNumber',
  'EmergencyContactName',
  'EmergencyContactPhoneNumber',
  'EmergencyContactRelationship',
  'City',
  'ZipCode',
  'Address',
] as const;

const isBlank = (value: unknown) =>
  Array.isArray(value) ? value.length === 0 : !(typeof value === 'string' && value.trim());

/** Adds a "required" issue for every extended field this tab owns that is still blank. */
const requireExtendedFields =
  (shape: object) => (values: Record<string, unknown>, ctx: z.RefinementCtx) => {
    EXTENDED_PROFILE_REQUIRED_FIELDS.filter((field) => field in shape).forEach((field) => {
      if (isBlank(values[field]))
        ctx.addIssue({ code: 'custom', path: [field], message: 'ERR_MSG_REQUIRED' });
    });
  };

export const selfOverviewExtendedSchema = selfOverviewSchema.superRefine(
  requireExtendedFields(selfOverviewSchema.shape)
);

/** The business email is the login identity HR owns: shown read-only to the person and never validated or saved by them. */
export const selfContactSchema = contactSchema.extend({ Email: z.string() });

export const selfContactExtendedSchema = selfContactSchema.superRefine(
  requireExtendedFields(selfContactSchema.shape)
);

/** The contact fields a person may change about themselves (everything but the business email). */
export const selfContactValuesToInput = (values: ContactValues): Record<string, unknown> => {
  const input = contactValuesToInput(values);
  delete input.Email;
  return input;
};

/** A record (or a save) with its emergency contact and present address read as the flat form fields. */
const withFlatContact = (record: Record<string, unknown>) => ({
  ...record,
  ...('EmergencyContact' in record
    ? emergencyContactValues(record.EmergencyContact as EmergencyContact | null)
    : {}),
  ...('PresentAddress' in record
    ? presentAddressValues(record.PresentAddress as EmployeeAddress | null)
    : {}),
});

/**
 * `IsProfileCompleted` after a person saved one of their own tabs (`merged` is their record with the save
 * applied). Someone who must provide extended info is complete once every required field is filled;
 * anyone else as soon as they saved their own profile once.
 */
export const isProfileCompleteAfterSelfSave = (
  merged: Record<string, unknown>,
  requiresExtendedInfo: boolean
) => {
  const flat: Record<string, unknown> = withFlatContact(merged);
  return (
    !requiresExtendedInfo ||
    EXTENDED_PROFILE_REQUIRED_FIELDS.every((field) => !isBlank(flat[field]))
  );
};

/** The basic-info fields a person may change about themselves (see `HR_OWNED_FIELDS` for what they may not). */
export const selfOverviewValuesToInput = (values: OverviewValues): Record<string, unknown> => {
  const input: Record<string, unknown> = { ...basicInfoToPayload(values) };
  HR_OWNED_FIELDS.forEach((field) => delete input[field]);
  return input;
};

export const selfSkillsSchema = z.object(skillsShape);
export type SelfSkillsValues = z.infer<typeof selfSkillsSchema>;

export interface SkillsFormLookups {
  subCategories: { ItemId: string; Title: string }[];
  skills: SkillLookup[];
}

/**
 * Skills as stored may point at a legacy title / key / name; they are matched to today's ids once the
 * lookups are loaded. An old skill is matched by name within the person's sub-categories; one that
 * matches nothing keeps its stored value and the name it was saved under.
 */
export const employeeToSkillsValues = (
  employee: Employee,
  lookups: SkillsFormLookups
): SelfSkillsValues => {
  const skills = skillsFromEmployee(employee);
  const subCategoryExperiences = skills.SubCategoryExperiences.map((entry) => ({
    ...entry,
    SubCategoryId: resolveSubCategoryId(entry.SubCategoryId, lookups.subCategories),
  }));
  const subCategoryIds = subCategoryExperiences.map((entry) => entry.SubCategoryId);
  const resolved = skills.Skills.map((entry) => {
    const skill = resolveStoredSkill(entry, lookups.skills, subCategoryIds);
    return skill
      ? { ...entry, Skill: skill.ItemId, SkillName: skill.Name }
      : { ...entry, SkillName: storedSkillName(entry) };
  });
  return {
    CategoryId: skills.CategoryId,
    SubCategoryExperiences: subCategoryExperiences,
    // Two old entries can resolve to the same skill; the first one stays.
    Skills: resolved.filter(
      (entry, index) => resolved.findIndex((other) => other.Skill === entry.Skill) === index
    ),
  };
};

export const skillsValuesToInput = (values: SelfSkillsValues, lookups: SkillsFormLookups) =>
  skillsToPayload(values, lookups.subCategories, lookups.skills);

/** The skill ids a Skills form opened with: the skills step and its save load them by these. */
export const savedSkillIdsOf = (values: Pick<SelfSkillsValues, 'Skills'>) =>
  values.Skills.map((entry) => entry.Skill).filter(Boolean);
